/** biome-ignore-all lint/style/useComponentExportOnlyModules: storybook export pattern */
'use client'

import * as React from 'react'
import { sidebarMenuButtonVariants } from '../lib/sidebar-menu-button'
import { type VariantProps } from 'class-variance-authority'
import { useKeyboardShortcut } from '@w/utils-fe'
import { PanelLeftIcon } from './icon'
import { cn } from '../lib/utils'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './sheet'
import { useIsMobile } from '../hooks/use-mobile'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip'
import { IconButton } from './icon-button'
import { Separator } from './separator'
import { Skeleton } from './skeleton'

const SIDEBAR_WIDTH = '20rem'
const SIDEBAR_WIDTH_MOBILE = '18rem'
const SIDEBAR_WIDTH_ICON = '6rem'
const SIDEBAR_KEYBOARD_SHORTCUT = 'b'
const SIDEBAR_HOVER_CLOSE_DELAY = 300

type RenderElementProps = {
  className?: string
  children?: React.ReactNode
  [key: string]: unknown
}
type RenderElement = React.ReactElement<RenderElementProps>

type SidebarContextProps = {
  state: 'expanded' | 'collapsed'
  open: boolean
  pinned: boolean
  setOpen: (open: boolean) => void
  revealOnHover: boolean
  setHoverOpen: (open: boolean) => void
  setFocusOpen: (open: boolean) => void
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  toggleSidebar: () => void
}

const SidebarContext = React.createContext<SidebarContextProps | null>(null)

const SidebarSideContext = React.createContext<'left' | 'right'>('left')

function useSidebar() {
  const ctx = React.useContext(SidebarContext)
  if (!ctx) throw new Error('useSidebar must be used within a SidebarProvider.')

  return ctx
}

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  revealOnHover = false,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  defaultOpen?: boolean
  /** Persistent desktop state. Hover/focus may reveal an unpinned sidebar. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Desktop: clicking the edge toggles pinned/hover mode. Mobile stays click-only. */
  revealOnHover?: boolean
}) {
  const isMobile = useIsMobile()
  const [openMobile, setOpenMobile] = React.useState(false)
  const [hoverOpen, setHoverOpen] = React.useState(false)
  const [focusOpen, setFocusOpen] = React.useState(false)

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen)
  const pinned = openProp ?? _open
  const open = pinned || (revealOnHover && (hoverOpen || focusOpen))
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === 'function' ? value(pinned) : value
      if (setOpenProp) setOpenProp(openState)
      else _setOpen(openState)
    },
    [setOpenProp, pinned],
  )

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    if (isMobile) {
      setOpenMobile((open) => !open)
    } else {
      setOpen((open) => !open)
    }
  }, [isMobile, setOpen])

  // Adds a keyboard shortcut to toggle the sidebar. Events an editor already
  // handled or typed into an editable element stay with that element
  // (tinykeys / react-hotkeys-hook skip form fields the same way).
  useKeyboardShortcut({
    [SIDEBAR_KEYBOARD_SHORTCUT]: (event) => {
      if (!(event.metaKey || event.ctrlKey)) return
      if (event.defaultPrevented || event.textEntry) return
      event.preventDefault()
      toggleSidebar()
    },
  })

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? 'expanded' : 'collapsed'

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      pinned,
      setOpen,
      revealOnHover,
      setHoverOpen,
      setFocusOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [
      state,
      open,
      pinned,
      setOpen,
      revealOnHover,
      isMobile,
      openMobile,
      toggleSidebar,
    ],
  )

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delay={0}>
        <div
          data-slot="sidebar-wrapper"
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH,
              '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={cn(
            'group/sidebar-wrapper has-data-[variant=inset]:bg-surface-3 flex min-h-svh w-full',
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  )
}

function Sidebar({
  side = 'left',
  variant = 'sidebar',
  collapsible = 'offcanvas',
  className,
  children,
  onMouseEnter,
  onMouseLeave,
  onFocusCapture,
  onBlurCapture,
  ...props
}: React.ComponentProps<'div'> & {
  side?: 'left' | 'right'
  variant?: 'sidebar' | 'floating' | 'inset'
  collapsible?: 'offcanvas' | 'icon' | 'none'
}) {
  const {
    isMobile,
    state,
    pinned,
    revealOnHover,
    setHoverOpen,
    setFocusOpen,
    openMobile,
    setOpenMobile,
  } = useSidebar()
  const closeTimeoutRef = React.useRef(0)

  const clearCloseTimeout = () => {
    window.clearTimeout(closeTimeoutRef.current)
  }
  const revealFromHover = () => {
    clearCloseTimeout()
    setHoverOpen(true)
  }
  const hideAfterHover = () => {
    clearCloseTimeout()
    closeTimeoutRef.current = window.setTimeout(() => {
      setHoverOpen(false)
    }, SIDEBAR_HOVER_CLOSE_DELAY)
  }

  React.useEffect(() => {
    return () => {
      window.clearTimeout(closeTimeoutRef.current)
      setHoverOpen(false)
      setFocusOpen(false)
    }
  }, [setFocusOpen, setHoverOpen])

  if (collapsible === 'none') {
    return (
      <SidebarSideContext.Provider value={side}>
        <div
          data-slot="sidebar"
          className={cn(
            'bg-surface-2 text-on-surface flex h-svh w-(--sidebar-width) flex-col p-4',
            side === 'left' && 'rounded-r-[28px]',
            side === 'right' && 'rounded-l-[28px]',
            className,
          )}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onFocusCapture={onFocusCapture}
          onBlurCapture={onBlurCapture}
          {...props}
        >
          {children}
        </div>
      </SidebarSideContext.Provider>
    )
  }

  if (isMobile) {
    return (
      <SidebarSideContext.Provider value={side}>
        <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
          <SheetContent
            data-sidebar="sidebar"
            data-slot="sidebar"
            data-mobile="true"
            className="bg-surface-2 text-on-surface w-(--sidebar-width) p-0"
            style={
              {
                '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
              } as React.CSSProperties
            }
            side={side}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Sidebar</SheetTitle>
              <SheetDescription>Displays the mobile sidebar.</SheetDescription>
            </SheetHeader>
            <div className="flex h-full w-full flex-col p-4">{children}</div>
          </SheetContent>
        </Sheet>
      </SidebarSideContext.Provider>
    )
  }

  return (
    <SidebarSideContext.Provider value={side}>
      <div
        className="group peer text-on-surface hidden md:block"
        data-state={state}
        data-collapsible={state === 'collapsed' ? collapsible : ''}
        data-variant={variant}
        data-side={side}
        data-pinned={pinned}
        data-hover-reveal={revealOnHover}
        data-slot="sidebar"
      >
        {revealOnHover && !pinned && state === 'collapsed' && (
          <div
            data-sidebar="hover-target"
            data-slot="sidebar-hover-target"
            aria-hidden="true"
            onMouseEnter={revealFromHover}
            className={cn(
              'fixed inset-y-0 z-10 hidden w-20 md:block',
              side === 'left' ? 'left-0' : 'right-0',
            )}
          />
        )}
        {/* This is what handles the sidebar gap on desktop */}
        <div
          data-slot="sidebar-gap"
          className={cn(
            'relative w-(--sidebar-width) bg-transparent transition-[width] duration-350',
            collapsible === 'icon' ? 'ease-in-out' : 'ease-out',
            'group-data-[collapsible=offcanvas]:w-0',
            'group-data-[hover-reveal=true]:group-data-[pinned=false]:w-0',
            'group-data-[side=right]:rotate-180',
            variant === 'floating' || variant === 'inset'
              ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
              : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)',
          )}
        />
        <div
          data-slot="sidebar-container"
          className={cn(
            'fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[translate,width] duration-350 will-change-transform md:flex',
            collapsible === 'icon' ? 'ease-in-out' : 'ease-out',
            side === 'left'
              ? 'left-0 group-data-[collapsible=offcanvas]:-translate-x-full'
              : 'right-0 group-data-[collapsible=offcanvas]:translate-x-full',
            variant === 'floating' || variant === 'inset'
              ? 'p-4 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
              : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)',
            className,
          )}
          onMouseEnter={(event) => {
            onMouseEnter?.(event)
            if (revealOnHover) revealFromHover()
          }}
          onMouseLeave={(event) => {
            onMouseLeave?.(event)
            if (revealOnHover) hideAfterHover()
          }}
          onFocusCapture={(event) => {
            onFocusCapture?.(event)
            if (
              revealOnHover &&
              !event.target.closest('[data-slot="sidebar-rail"]')
            )
              setFocusOpen(true)
          }}
          onBlurCapture={(event) => {
            onBlurCapture?.(event)
            const nextTarget = event.relatedTarget
            if (
              revealOnHover &&
              (!(nextTarget instanceof Node) ||
                !event.currentTarget.contains(nextTarget))
            )
              setFocusOpen(false)
          }}
          {...props}
        >
          <div
            data-sidebar="sidebar"
            data-slot="sidebar-inner"
            className={cn(
              'bg-surface-2 flex h-full w-full min-w-0 flex-col p-4',
              'group-data-[variant=floating]:rounded-[28px]',
              side === 'left' && 'rounded-r-[28px]',
              side === 'right' && 'rounded-l-[28px]',
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </SidebarSideContext.Provider>
  )
}

function SidebarTrigger({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof IconButton>) {
  const { toggleSidebar } = useSidebar()

  return (
    <IconButton
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      className={cn('size-7', className)}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}
    >
      <PanelLeftIcon />
      Toggle Sidebar
    </IconButton>
  )
}

// This is full-height rail that is used to toggle the sidebar:
function SidebarRail({ className, ...props }: React.ComponentProps<'button'>) {
  const { toggleSidebar } = useSidebar()
  const side = React.useContext(SidebarSideContext)

  const button = (
    <button
      data-sidebar="rail"
      data-slot="sidebar-rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      className={cn(
        'hover:after:bg-border absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-[opacity] ease-in-out group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:top-8 after:bottom-8 after:left-1/2 after:w-[2px] after:rounded-full sm:flex',
        'in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize',
        '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
        'hover:group-data-[collapsible=offcanvas]:bg-surface-2 group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full',
        '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
        '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
        className,
      )}
      {...props}
    />
  )

  return (
    <Tooltip>
      <TooltipTrigger render={button} />
      <TooltipContent side={side === 'right' ? 'left' : 'right'}>
        Toggle Sidebar
      </TooltipContent>
    </Tooltip>
  )
}

function SidebarInset({ className, ...props }: React.ComponentProps<'main'>) {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        'bg-surface-1 relative flex w-full flex-1 flex-col',
        'md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-[28px] md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2',
        className,
      )}
      {...props}
    />
  )
}

// function SidebarInput({
//   className,
//   ...props
// }: React.ComponentProps<typeof Input>) {
//   return (
//     <Input
//       data-slot="sidebar-input"
//       data-sidebar="input"
//       className={cn('bg-surface-1 h-8 w-full shadow-none', className)}
//       {...props}
//     />
//   )
// }

function SidebarInput({ className, ...props }: React.ComponentProps<'input'>) {
  return (
    <input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn('bg-surface-2 h-8 w-full shadow-none', className)}
      {...props}
    />
  )
}

function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn('mb-2 flex flex-col gap-2', className)}
      {...props}
    />
  )
}

function SidebarFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn('flex flex-col gap-2', className)}
      {...props}
    />
  )
}

function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={className}
      {...props}
    />
  )
}

function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto',
        className,
      )}
      {...props}
    />
  )
}

function SidebarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn('relative flex w-full min-w-0 flex-col', className)}
      {...props}
    />
  )
}

function SidebarGroupLabel({
  className,
  render,
  children,
  ...props
}: React.ComponentProps<'div'> & { render?: RenderElement }) {
  const sharedProps = {
    'data-slot': 'sidebar-group-label',
    'data-sidebar': 'group-label',
    className: cn(
      'text-on-surface-secondary ring-primary/80 mb-2 flex shrink-0 items-center justify-center rounded-md text-xs font-medium outline-hidden transition-[margin,opacity] duration-200 ease-in-out select-none focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
      'group-data-[collapsible=icon]:hidden',
      className,
    ),
    ...props,
  }

  if (render) {
    return React.cloneElement(
      render,
      {
        ...sharedProps,
        className: cn(sharedProps.className, render.props?.className),
      },
      children ?? render.props?.children,
    )
  }

  return <div {...sharedProps}>{children}</div>
}

function SidebarGroupAction({
  className,
  render,
  children,
  ...props
}: React.ComponentProps<'button'> & { render?: RenderElement }) {
  const sharedProps = {
    'data-slot': 'sidebar-group-action',
    'data-sidebar': 'group-action',
    className: cn(
      'text-on-surface ring-primary/80 hover:bg-primary/8 hover:text-on-surface absolute -top-2.5 right-3 flex aspect-square items-center justify-center outline-hidden transition-transform focus-visible:ring-2 [&_svg:not([class*="size-"])]:size-4 [&>svg]:shrink-0',
      // Increases the hit area of the button on mobile.
      'after:absolute after:-inset-2 md:after:hidden',
      'group-data-[collapsible=icon]:hidden',
      className,
    ),
    ...props,
  }

  if (render) {
    return React.cloneElement(
      render,
      {
        ...sharedProps,
        className: cn(sharedProps.className, render.props?.className),
      },
      children ?? render.props?.children,
    )
  }

  return <button {...sharedProps}>{children}</button>
}

function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn('w-full text-sm', className)}
      {...props}
    />
  )
}

function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn('flex w-full min-w-0 flex-col', className)}
      {...props}
    />
  )
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn(
        'group/menu-item relative cursor-pointer select-none',
        className,
      )}
      {...props}
    />
  )
}


function SidebarMenuButton({
  render,
  isActive = false,
  variant = 'default',
  size = 'default',
  tooltip,
  className,
  children,
  ...props
}: React.ComponentProps<'button'> & {
  render?: RenderElement
  isActive?: boolean
  tooltip?: string | React.ComponentProps<typeof TooltipContent>
} & VariantProps<typeof sidebarMenuButtonVariants>) {
  const { isMobile, state } = useSidebar()
  const side = React.useContext(SidebarSideContext)

  const sharedProps = {
    'data-slot': 'sidebar-menu-button',
    'data-sidebar': 'menu-button',
    'data-size': size,
    'data-active': isActive,
    className: cn(
      sidebarMenuButtonVariants({ variant, size }),
      'cursor-pointer select-none',
      className,
    ),
    ...props,
  }

  const button = render ? (
    React.cloneElement(
      render,
      {
        ...sharedProps,
        className: cn(sharedProps.className, render.props?.className),
      },
      children ?? render.props?.children,
    )
  ) : (
    <button {...sharedProps}>{children}</button>
  )

  if (!tooltip) return button
  if (typeof tooltip === 'string') tooltip = { children: tooltip }
  return (
    <Tooltip>
      <TooltipTrigger render={button} />
      <TooltipContent
        side={side === 'right' ? 'left' : 'right'}
        align="center"
        hidden={state !== 'collapsed' || isMobile}
        {...tooltip}
      />
    </Tooltip>
  )
}

function SidebarMenuAction({
  className,
  render,
  showOnHover = false,
  children,
  ...props
}: React.ComponentProps<'button'> & {
  render?: RenderElement
  showOnHover?: boolean
}) {
  const sharedProps = {
    'data-slot': 'sidebar-menu-action',
    'data-sidebar': 'menu-action',
    className: cn(
      'text-on-surface ring-primary/80 hover:bg-primary/8 absolute right-2 bottom-2 flex aspect-square items-center justify-center outline-hidden transition-transform focus-visible:ring-2 [&_svg:not([class*="size-"])]:size-4 [&>svg]:shrink-0',
      // Increases the hit area of the button on mobile.
      'after:absolute after:-inset-2 md:after:hidden',
      'peer-data-[size=sm]/menu-button:top-1',
      'peer-data-[size=default]/menu-button:top-1.5',
      'peer-data-[size=lg]/menu-button:top-2.5',
      'group-data-[collapsible=icon]:hidden',
      showOnHover &&
        'peer-data-[active=true]/menu-button:text-on-surface group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0',
      className,
    ),
    ...props,
  }

  if (render) {
    return React.cloneElement(
      render,
      {
        ...sharedProps,
        className: cn(sharedProps.className, render.props?.className),
      },
      children ?? render.props?.children,
    )
  }

  return <button {...sharedProps}>{children}</button>
}

function SidebarMenuBadge({
  className,
  render,
  children,
  ...props
}: React.ComponentProps<'div'> & { render?: RenderElement }) {
  const sharedProps = {
    'data-slot': 'sidebar-menu-badge',
    'data-sidebar': 'menu-badge',
    className: cn(
      'text-light-utility! bg-error ml-auto flex size-6 items-center justify-center rounded-full text-xs',
      'peer-hover/menu-button:text-on-surface peer-data-[active=true]/menu-button:text-on-surface',
      'group-data-[collapsible=icon]:hidden',
      className,
    ),
    ...props,
  }

  if (render) {
    return React.cloneElement(
      render,
      {
        ...sharedProps,
        className: cn(sharedProps.className, render.props?.className),
      },
      children ?? render.props?.children,
    )
  }

  return <div {...sharedProps}>{children}</div>
}

function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<'div'> & {
  showIcon?: boolean
}) {
  // Random width between 50 to 90%.
  const [width] = React.useState(
    () => `${Math.floor(Math.random() * 40) + 50}%`,
  )

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn('flex h-8 items-center gap-2 rounded-md px-2', className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            '--skeleton-width': width,
          } as React.CSSProperties
        }
      />
    </div>
  )
}

function SidebarMenuSub({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        'border-border mx-3.5 flex min-w-0 flex-col border-l px-2.5 py-0.5',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  )
}

function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn('group/menu-sub-item relative', className)}
      {...props}
    />
  )
}

function SidebarMenuSubButton({
  render,
  size = 'md',
  isActive = false,
  className,
  children,
  ...props
}: React.ComponentProps<'a'> & {
  render?: RenderElement
  size?: 'sm' | 'md'
  isActive?: boolean
}) {
  const sharedProps = {
    'data-slot': 'sidebar-menu-sub-button',
    'data-sidebar': 'menu-sub-button',
    'data-size': size,
    'data-active': isActive,
    className: cn(
      'text-on-surface ring-primary/80 hover:bg-primary/8 hover:text-on-surface active:bg-surface-3 active:text-on-surface [&>svg]:text-on-surface flex -translate-x-px cursor-pointer items-center gap-2 overflow-hidden rounded-full p-4 outline-hidden select-none focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-6 [&>svg]:shrink-0',
      'data-[active=true]:bg-primary/8 data-[active=true]:text-on-surface',
      size === 'sm' && 'text-xs',
      size === 'md' && 'text-sm',
      'group-data-[collapsible=icon]:hidden',
      className,
    ),
    ...props,
  }

  if (render) {
    return React.cloneElement(
      render,
      {
        ...sharedProps,
        className: cn(sharedProps.className, render.props?.className),
      },
      children ?? render.props?.children,
    )
  }

  return <a {...sharedProps}>{children}</a>
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
}
